"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useRef, useEffect } from "react";

interface Service {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  url: string;
}

const Services: Service[] = [
  {
    title: "General Dentistry",
    imageSrc: "/images/custom_modules_home_expertise_images_icon1.webp",
    imageAlt: "Icon for General Dentistry services",
    description:
      "Routine cleanings, exams, and preventive treatments to maintain your oral health.",
    url: "/services/dental-cleanings/",
  },
  {
    title: "Emergency Care",
    imageSrc: "/images/custom_modules_home_expertise_images_icon5.webp",
    imageAlt: "Icon for Emergency Care services",
    description:
      "Prompt treatment for sudden toothaches, broken teeth, and other dental emergencies.",
    url: "/services/emergency-treatment/",
  },
  {
    title: "Teeth Whitening",
    imageSrc: "/images/teeth-whitening-gold-icon.png",
    imageAlt: "Icon for Teeth Whitening services",
    description:
      "Brighten your smile with our professional whitening services.",
    url: "/services/teeth-whitening/",
  },
  {
    title: "Dental Implants",
    imageSrc: "/images/custom_modules_home_expertise_images_icon2.webp",
    imageAlt: "Icon for Dental Implants services",
    description:
      "Natural-looking replacements for missing teeth that restore function and confidence.",
    url: "/services/implant-dentistry/",
  },
  {
    title: "Orthodontics",
    imageSrc: "/images/custom_modules_home_expertise_images_icon4.webp",
    imageAlt: "Icon for Orthodontics services",
    description:
      "Aligners and braces to straighten teeth and improve bite alignment.",
    url: "/services/invisalign/",
  },
  {
    title: "Cosmetic Procedures",
    imageSrc: "/images/custom_modules_home_expertise_images_icon7.webp",
    imageAlt: "Icon for Cosmetic Procedures services",
    description:
      "Veneers, bonding, and other treatments to enhance the appearance of your smile.",
    url: "/services/cosmetic-dentistry/",
  },
  {
    title: "Pediatric Dentistry",
    imageSrc: "/images/custom_modules_home_expertise_images_icon3.webp",
    imageAlt: "Icon for Pediatric Dentistry services",
    description:
      "Gentle care tailored to the dental needs of children and adolescents.",
    url: "/services/pediatric-dentistry/",
  },
  {
    title: "Root Canal Therapy",
    imageSrc: "/images/custom_modules_home_expertise_images_icon6.webp",
    imageAlt: "Icon for Root Canal Therapy services",
    description:
      "Relieve pain and save damaged teeth with our expert root canal treatments.",
    url: "/services/root-canals/",
  },
];

export default function IconGrid() {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full z-10"
      role="region"
      aria-labelledby="services-heading"
    >
      <div className="relative w-full overflow-hidden bg-gray-950 text-white">
        {isInView && (
          <div className="absolute inset-0 z-0 h-full">
            <Image
              src="/images/custom_images_banner.webp"
              alt="Smile4Me dental office background"
              fill
              className="object-cover object-center"
              sizes="100vw"
              quality={50}
              priority={false}
            />
            <div className="absolute inset-0 bg-black/65"></div>
          </div>
        )}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 pt-16 sm:pt-20 pb-6">
          <div className="max-w-4xl mx-auto mb-10 text-center">
            <h2
              id="services-heading"
              className="
                text-4xl md:text-5xl font-bold text-white
                pb-2 after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:my-5
                after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]
              "
            >
              Dental Services We Offer
            </h2>
            <p className="text-md md:text-lg leading-relaxed text-gray-200">
              At Smile4Me Dental Care, we offer a full range of dental services for patients of all ages.
              Whatever your smile needs,{" "}
              <Link
                href="/our-team/"
                className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
              >
                our Astoria dental team
              </Link>{" "}
              is here to help.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8 sm:gap-x-8 sm:gap-y-10">
            {Services.map(({ title, imageSrc, imageAlt, description, url }, index) => (
              <Link
                key={title}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  flex min-h-[178px] min-w-[44px] flex-col items-center text-center p-4
                  transform transition-transform duration-300
                  hover:-translate-y-2
                  rounded-lg hover:bg-white/10
                  focus:outline-none focus:ring-2 focus:ring-[#ECD05B] focus:ring-opacity-50
                "
                aria-label={`Learn more about ${title}`}
              >
                <Image
                  src={imageSrc}
                  alt={imageAlt}
                  width={72}
                  height={72}
                  className="mb-3 h-[72px] w-[72px] object-contain"
                  sizes="72px"
                  quality={75}
                  loading={index < 4 ? "eager" : "lazy"}
                />
                <h4 className="text-sm sm:text-base font-semibold mb-2 text-[#C7973D]">
                  {title}
                </h4>
                <p className="max-w-[210px] text-sm sm:text-base leading-relaxed text-white">
                  {description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
